import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  TextField,
  ReferenceField,
  ListBase,
  ListToolbar,
  Pagination,
  useRefresh,
  useListContext,
  Title,
  ListContextProvider,
  FunctionField,
  BooleanField,
} from 'react-admin';

import { Card, Divider, Tabs, Tab } from '@material-ui/core';
import { useSnackbar } from 'notistack';

import { DialogoConfirmacao } from '../../Components';
import Datagrid from '../../Components/Datagrid';
import DialogoDeleteResource from '../../Components/DialogoDeleteResource';
import {
  api,
  imprimirChecklistColaboradorPuro,
  getUserId,
} from '../../services';
import {
  STATUS_ATIVO,
  STATUS_INATIVO,
  TURNO_MANHA,
  TURNO_NOITE,
  TURNO_12_HORAS,
} from '../../utils/constants';
import ActionsField from './ActionsField';
import Actions from './ActionsList';
import Dialogo from './DialogoCreateEdit';
import DialogoHorarios from './DialogoHorarios';
import DialogoShow from './DialogoShow';
import Filter from './Filter';

const tabs = [
  { id: 'ativos', name: 'Ativos' },
  { id: 'inativos', name: 'Inativos' },
];

const optionsTurno = {
  [TURNO_MANHA]: 'Manhã',
  [TURNO_NOITE]: 'Noite',
  [TURNO_12_HORAS]: '12H',
};

const MyList = ({ ...props }) => {
  const refresh = useRefresh();
  const refDialogoCompra = useRef(null);
  const refDialogoDeleteResource = useRef(null);
  const refDialogoShow = useRef(null);
  const refDialogoConfirmacao = useRef(null);
  const listContext = useListContext();
  const { ids, filterValues, setFilters, displayedFilters } = listContext;
  const [ativos, setAtivos] = useState([]);
  const [inativos, setInativos] = useState([]);
  const { enqueueSnackbar } = useSnackbar();

  const refDialogoHorarios = useRef(null);

  function handleCriar() {
    if (refDialogoCompra.current) {
      refDialogoCompra.current.handleCreate();
    }
  }

  function handleEdit({ id }) {
    if (refDialogoCompra.current) {
      refDialogoCompra.current.handleEdit(id);
    }
  }

  function handleShow({ id }) {
    if (refDialogoShow.current) {
      refDialogoShow.current.handleOpen(id);
    }
  }

  function handleAtivar({ id }) {
    if (refDialogoConfirmacao.current) {
      refDialogoConfirmacao.current.handleOpen(
        'Reativação de Colaborador',
        'Deseja realmente reativar este colaborador?',
        { id, codigo: 1 },
      );
    }
  }

  function handleDesativar({ id }) {
    if (refDialogoConfirmacao.current) {
      refDialogoConfirmacao.current.handleOpen(
        'Desativação de Colaborador',
        'Deseja realmente desativar este colaborador?',
        { id, codigo: 2 },
      );
    }
  }

  function handleDeletar({ id }) {
    if (refDialogoConfirmacao.current) {
      refDialogoConfirmacao.current.handleOpen(
        'Exclusão de Colaborador',
        'Deseja deletar permanentemente este colaborador?',
        { id, codigo: 3 },
      );
    }
  }

  async function ativar(id) {
    await api.put(`/colaboradores/status/ativar/${id}`);
    handleClose();
  }

  async function desativar(id) {
    try {
      const response = await api.put(`/colaboradores/status/inativar/${id}`);
      const { message } = response.data;
      if (message) throw message;
      handleClose();
    } catch (e) {
      enqueueSnackbar(`${e}`, {
        variant: 'error',
      });
    }
  }

  async function deletar(id) {
    await api.put(`/colaboradores/status/deletar/${id}`);
    handleClose();
  }

  async function handleConfirma({ id, codigo }) {
    switch (codigo) {
      case 1:
        ativar(id);
        break;
      case 2:
        desativar(id);
        break;
      case 3:
        deletar(id);
        break;
      default:
        break;
    }
  }

  useEffect(() => {
    if (ids && ids !== filterValues.status) {
      switch (filterValues.status) {
        case STATUS_ATIVO:
          setAtivos(ids);
          break;
        case STATUS_INATIVO:
          setInativos(ids);
          break;
        default:
          break;
      }
    }
  }, [ids, filterValues.status]);

  const handleChange = useCallback(
    (event, value) => {
      if (setFilters) {
        setFilters(
          {
            ...filterValues,
            status: value === 'ativos' ? STATUS_ATIVO : STATUS_INATIVO,
          },
          displayedFilters,
        );
      }
    },
    [displayedFilters, filterValues, setFilters],
  );

  function handleClose() {
    refresh();
  }

  function handleHorarios({ id }) {
    if (refDialogoHorarios.current) {
      refDialogoHorarios.current.handleOpen(id);
    }
  }

  async function handleImprimir({ id }) {
    const codigo = await imprimirChecklistColaboradorPuro(id);
    if (codigo < 0) {
      enqueueSnackbar('Erro ao imprimir o checklist!', {
        variant: 'error',
      });
    }
  }

  return (
    <>
      <Title title="Colaboradores" />
      <ListToolbar
        filters={props.filters}
        actions={<Actions handleCriar={handleCriar} {...props} />}
        {...props}
      />
      <Card style={{ borderRadius: '20px' }} {...props}>
        <Tabs
          variant="fullWidth"
          centered
          value={filterValues.status === STATUS_ATIVO ? tabs[0].id : tabs[1].id}
          indicatorColor="primary"
          onChange={handleChange}
        >
          {tabs.map((choice) => (
            <Tab key={choice.id} label={choice.name} value={choice.id} />
          ))}
        </Tabs>
        <Divider />
        <div>
          {filterValues.status === STATUS_ATIVO && (
            <ListContextProvider value={{ ...listContext, ids: ativos }}>
              <Datagrid {...props}>
                <TextField source="nome" label="Nome" />
                <ReferenceField
                  label="Usuário"
                  source="user_id"
                  reference="users"
                  sortable={false}
                >
                  <TextField source="username" />
                </ReferenceField>
                <ReferenceField
                  label="Função"
                  source="funcao_colaborador_id"
                  reference="funcoes_colaboradores"
                  sortable={false}
                >
                  <TextField source="nome" />
                </ReferenceField>
                <ReferenceField
                  label="Grupo"
                  source="grupo_colaborador_id"
                  reference="grupos_colaborador"
                  sortable={false}
                >
                  <TextField source="nome" />
                </ReferenceField>
                <ReferenceField
                  label="Codinome"
                  source="codinome_colaborador_id"
                  reference="codinomes_colaborador"
                  sortable={false}
                >
                  <TextField source="nome" />
                </ReferenceField>
                <FunctionField
                  label="Turno"
                  render={(record) => optionsTurno[record.turno]}
                />
                <BooleanField source="is_gestor" label="Gestor" />
                <ActionsField
                  source="valor"
                  textAlign="right"
                  label="Ações"
                  sortable={false}
                  handleEdit={handleEdit}
                  handleShow={handleShow}
                  handleAtivar={handleAtivar}
                  handleDesativar={handleDesativar}
                  handleDeletar={handleDeletar}
                  handleHorarios={handleHorarios}
                  handleImprimir={handleImprimir}
                />
              </Datagrid>
            </ListContextProvider>
          )}
          {filterValues.status === STATUS_INATIVO && (
            <ListContextProvider value={{ ...listContext, ids: inativos }}>
              <Datagrid {...props}>
                <TextField source="nome" label="Nome" />
                <ReferenceField
                  label="Usuário"
                  source="user_id"
                  reference="users"
                  sortable={false}
                >
                  <TextField source="username" />
                </ReferenceField>
                <ReferenceField
                  label="Função"
                  source="funcao_colaborador_id"
                  reference="funcoes_colaboradores"
                  sortable={false}
                >
                  <TextField source="nome" />
                </ReferenceField>
                <ReferenceField
                  label="Grupo"
                  source="grupo_colaborador_id"
                  reference="grupos_colaborador"
                  sortable={false}
                >
                  <TextField source="nome" />
                </ReferenceField>
                <FunctionField
                  label="Turno"
                  render={(record) => optionsTurno[record.turno]}
                />
                <BooleanField source="is_gestor" label="Gestor" />
                <ActionsField
                  source="valor"
                  textAlign="right"
                  label="Ações"
                  sortable={false}
                  handleEdit={handleEdit}
                  handleShow={handleShow}
                  handleAtivar={handleAtivar}
                  handleDesativar={handleDesativar}
                  handleDeletar={handleDeletar}
                  handleImprimir={handleImprimir}
                />
              </Datagrid>
            </ListContextProvider>
          )}
        </div>
        <TransacoesPagination />
      </Card>
      <Dialogo ref={refDialogoCompra} handleClose={handleClose} />
      <DialogoDeleteResource ref={refDialogoDeleteResource} />
      <DialogoShow ref={refDialogoShow} handleClose={handleClose} />
      <DialogoConfirmacao
        ref={refDialogoConfirmacao}
        handleConfirma={handleConfirma}
      />
      <DialogoHorarios ref={refDialogoHorarios} />
    </>
  );
};

const ContainerMyList = ({ children, ...props }) => (
  <>
    <ListBase {...props}>
      <MyList {...props} />
    </ListBase>
  </>
);

const TransacoesPagination = (props) => (
  <Pagination rowsPerPageOptions={[10, 25, 50, 100]} {...props} />
);

const List2 = (props) => (
  <>
    <ContainerMyList
      {...props}
      bulkActionButtons={false}
      pagination={<TransacoesPagination />}
      perPage={50}
      filters={<Filter />}
      title="Colaboradores"
      filterDefaultValues={{
        status: STATUS_ATIVO,
      }}
    />
  </>
);

export default List2;
